import React from 'react';
import { FormGroup, Row } from 'reactstrap';
import LargestCol from 'components/LargestCol';
import Label from 'components/Input/Label';
import { Field } from 'redux-form';
import RoomFeatures from 'routes/GuestInformation/components/RoomFeatures';
import SpecialRequests from 'routes/GuestInformation/components/SpecialRequests';
import AmenityRequests from './AmenityRequests';
import {
  FOOD_COMP,
  NO_FOOD_COMP,
  NO_SEND_AMENITIES,
  NO_SPECIAL_OCCASION,
  SEND_AMENITIES,
  SPECIAL_OCCASION
} from 'utils/constants';
import RenderedCustomInput from 'components/RenderedCustomInput';
import RenderedInput from 'components/RenderedInput';
import { maxLength, required, nonEmpty } from 'utils/validators';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const RadioButtonCol = styled(LargestCol)`
  flex-direction: row;

  .custom-control:not(:last-child) {
    margin-right: 1em;
  }
`;

const SelectCol = styled(LargestCol)`
  flex-direction: column;
`;

const SpecialOccasionCol = styled(SelectCol)`
  @media screen and (min-width: ${props => props.theme.breakpoints.medium}) {
    flex-direction: row;
  }
`;

const FoodCompRow = styled(FormGroup)`
  display: flex;
  padding-left: 0;
`;

const CompCreditCol = styled(LargestCol)`
  flex-direction: column;
  align-items: flex-start;

  @media screen and (min-width: ${props => props.theme.breakpoints.medium}) {
    flex-direction: row;
    align-items: center;
    margin-bottom: 0;

    ${Label} {
      padding-right: 1em;
    }
  }
`;

const SpecialOccasionRow = styled(FormGroup)`
  display: flex;
  padding-left: 0;

  ${Label} {
    flex-direction: column;
  }

  @media (max-width: ${props => props.theme.breakpoints.extraLarge}) {
    margin-top: 1.5rem;
  }
`;

const propTypes = {
  property: PropTypes.string.isRequired,
  guestInfo: PropTypes.any
};

function HostRoomOptions({ guestInfo, property }) {
  return (
    <React.Fragment>
      <Row>
        <SelectCol xs={12} sm={6}>
          <Label>Room Features Request</Label>
          <Field
            id="roomFeatures"
            name="roomFeatures"
            component={RoomFeatures}
            property={property}
          />
        </SelectCol>
        <SelectCol xs={12} sm={6}>
          <Label>Special Requests</Label>
          <Field
            id="specialRequests"
            name="specialRequests"
            component={SpecialRequests}
            property={property}
          />
        </SelectCol>
      </Row>
      <FoodCompRow row check>
        <Label xs={12} md={5}>
          Comp Routing Request
        </Label>
        <RadioButtonCol xs={12} md={7}>
          <Field
            id="compCreditYes"
            name="compCredit"
            type="radio"
            label="Yes"
            value={FOOD_COMP}
            component={RenderedCustomInput}
          />
          <Field
            id="compCreditNo"
            name="compCredit"
            type="radio"
            label="No"
            value={NO_FOOD_COMP}
            component={RenderedCustomInput}
          />
        </RadioButtonCol>
      </FoodCompRow>
      {guestInfo && guestInfo.compCredit === FOOD_COMP && (
        <FoodCompRow row>
          <CompCreditCol xs={12} sm={4}>
            <Label htmlFor="creditAmount">Amount</Label>
            <Field
              id="creditAmount"
              name="creditAmount"
              type="text"
              component={RenderedInput}
              validate={[maxLength(255), required, nonEmpty]}
            />
          </CompCreditCol>
          <CompCreditCol xs={12} sm={8}>
            <Label htmlFor="creditOutlet">Outlet</Label>
            <Field
              id="creditOutlet"
              name="creditOutlet"
              type="text"
              component={RenderedInput}
              validate={[maxLength(255), required, nonEmpty]}
            />
          </CompCreditCol>
        </FoodCompRow>
      )}
      <SpecialOccasionRow row check>
        <Label xs={12} md={5}>
          Special Occasion
        </Label>
        <RadioButtonCol xs={12} md={7}>
          <Field
            id="specialOccasion"
            name="specialOccasion"
            type="radio"
            label="Yes"
            value={SPECIAL_OCCASION}
            component={RenderedCustomInput}
          />
          <Field
            id="noSpecialOccasion"
            name="specialOccasion"
            type="radio"
            label="No"
            value={NO_SPECIAL_OCCASION}
            component={RenderedCustomInput}
          />
        </RadioButtonCol>
      </SpecialOccasionRow>
      {guestInfo && guestInfo.specialOccasion === SPECIAL_OCCASION && (
        <Row>
          <SpecialOccasionCol xs="12">
            <Label htmlFor="specialOccasionDetails">
              What occasion is the guest celebrating? Please note that requests
              made on the day of arrival should be called in to the Hotel.
            </Label>
            <Field
              id="specialOccasionDetails"
              name="specialOccasionDetails"
              type="text"
              component={RenderedInput}
              validate={[maxLength(255)]}
            />
          </SpecialOccasionCol>
        </Row>
      )}
      <SpecialOccasionRow row check>
        <Label xs={12} md={5}>
          Would you like to send an amenity to the guest?
          <br />
          <small>
            NOTE: Same-day reservation requests must be called directly to Room
            Service and not completed here.
          </small>
        </Label>
        <RadioButtonCol xs={12} md={7}>
          <Field
            id="sendAmenities"
            name="sendAmenities"
            type="radio"
            label="Yes"
            value={SEND_AMENITIES}
            component={RenderedCustomInput}
          />
          <Field
            id="noSendAmenities"
            name="sendAmenities"
            type="radio"
            label="No"
            value={NO_SEND_AMENITIES}
            component={RenderedCustomInput}
          />
        </RadioButtonCol>
      </SpecialOccasionRow>
      {guestInfo && guestInfo.sendAmenities === SEND_AMENITIES && (
        <Row>
          <LargestCol xs="12">
            <Label htmlFor="amenityRequests">
              Select the items to send to the guest.
            </Label>
            <Field
              id="amenityRequests"
              name="amenityRequests"
              component={AmenityRequests}
              property={property}
            />
          </LargestCol>
          <LargestCol xs="12">
            <Label htmlFor="customAmenityRequest">Custom Amenity Request</Label>
            <Field
              id="customAmenityRequest"
              name="customAmenityRequest"
              type="text"
              component={RenderedInput}
              validate={[maxLength(255)]}
            />
          </LargestCol>
        </Row>
      )}
    </React.Fragment>
  );
}

HostRoomOptions.propTypes = propTypes;

export default HostRoomOptions;
